import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as brokerApi from '@/api/broker.api';
import { toast } from '@components/ui/toast/Toast';
import { setBrokerStatus, updateSubscriptionStatus } from './authSlice';

import { updateLocal } from '@utils/storage';

const syncBrokerLoggedIn = (brokers, dispatch) => {
  const hasActive = brokers.some((b) => b.status === 'loggedIn');
  dispatch(setBrokerStatus(hasActive));
  updateLocal('user', { brokerLoggedIn: hasActive });
};

export const fetchBrokerList = createAsyncThunk(
  'broker/fetchList',
  async (_, { dispatch, rejectWithValue }) => {
    const res = await brokerApi.fetchBrokers();
    if (res?.code === 200) {
      const brokers = (res.data?.brokers ?? []).sort((a, b) =>
        a.type.localeCompare(b.type),
      );
      syncBrokerLoggedIn(brokers, dispatch);
      return brokers;
    }
    return rejectWithValue(res?.error ?? 'Failed to fetch brokers');
  },
);

export const checkBrokerLoginThunk = createAsyncThunk(
  'broker/checkLogin',
  async (_, { dispatch, rejectWithValue }) => {
    const res = await brokerApi.fetchLoginCheck();
    if (!res) return rejectWithValue('Login check failed');

    if (res?.code === 200) {
      const data = res.data;
      dispatch(setBrokerStatus(data.brokerLoggedIn));
      dispatch(
        updateSubscriptionStatus({
          activeSubscription: data.activeSubscription,
          subscriptionValidTill: data.subscriptionValidTill,
        }),
      );

      updateLocal('user', {
        brokerLoggedIn: data.brokerLoggedIn,
        activeSubscription: data.activeSubscription,
        subscriptionValidTill: data.subscriptionValidTill,
      });

      return data;
    }
    if (res?.code === 403) {
      dispatch(setBrokerStatus(false));
    }
    return rejectWithValue(res?.error || 'Login check failed');
  },
);



export const addBrokerThunk = createAsyncThunk(
  'broker/add',
  async (params, { dispatch, rejectWithValue }) => {
    const res = await brokerApi.createBroker(params);
    if (res?.code === 200) {
      toast.success('Broker added successfully');
      dispatch(fetchBrokerList());
      return res.data;
    }
    /* special handling for specific codes if needed */
    if (res?.code === 403) {
      return rejectWithValue('AUTH_REQUIRED');
    }
    return rejectWithValue(res?.error || 'Failed to add broker');
  },
);

export const deleteBroker = createAsyncThunk(
  'broker/delete',
  async (brokerId, { dispatch, rejectWithValue }) => {
    const res = await brokerApi.removeBrokerById(brokerId);
    if (res?.code === 200) {
      toast.success('Broker removed successfully');
      dispatch(fetchBrokerList());
      return brokerId;
    }
    return rejectWithValue(res?.error ?? 'Failed to remove broker');
  },
);

export const logoutBrokerThunk = createAsyncThunk(
  'broker/logout',
  async (broker, { dispatch, rejectWithValue }) => {
    const res = await brokerApi.logoutBroker(broker.id);
    if (res?.code === 200 || res?.data) {
      toast.success(`Broker ${broker.userName} logged out`);
      if (broker.quickTrade) {
        await brokerApi.updateBrokerSettings(broker.id, {
          quickTrade: false,
        });
      }
      dispatch(fetchBrokerList());
      return broker.id;
    }
    return rejectWithValue(res?.error ?? 'Logout failed');
  },
);

export const setBrokerPurposeThunk = createAsyncThunk(
  'broker/setPurpose',
  async ({ brokerId, purpose }, { dispatch, rejectWithValue }) => {
    const res = await brokerApi.setBrokerPurpose(brokerId, purpose);
    if (res?.code === 200) {
      dispatch(fetchBrokerList());
      return { brokerId, purpose };
    }
    return rejectWithValue(res?.error || 'Failed to set purpose');
  },
);



const brokerSlice = createSlice({
  name: 'broker',
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBrokerList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBrokerList.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = payload;
      })
      .addCase(fetchBrokerList.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });
  },
});

export default brokerSlice.reducer;